import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

/**
 * ResetPassword — completes the password-reset flow.
 *
 * The user lands here from the emailed magic link (/reset-password?token=…).
 * Posts the raw token + new password to POST /api/auth/reset-password.
 * On success we show a confirmation panel + bounce to /login; on error
 * (expired / already-used / missing token) we surface the backend
 * message and keep the form open so the user can request a fresh link.
 */
export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const tokenMissing = !token;

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (password.length < 6) {
            setErr("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            setErr("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            const r = await fetch(`${base}/api/auth/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, password }),
                credentials: "include",
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setErr(formatApiErrorDetail(data.detail) || "Couldn't reset your password. Please request a new link.");
                return;
            }
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 1600);
        } catch {
            setErr("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#03122b] px-6 text-white"
            data-testid="reset-page"
        >
            <div className="pointer-events-none absolute inset-0 grid-bg opacity-[0.12]" />
            <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-[#00C2FF]/12 blur-[160px]" />
            <div className="pointer-events-none absolute -right-40 bottom-1/4 h-[420px] w-[420px] rounded-full bg-[#5b5ef0]/14 blur-[160px]" />

            <div className="relative z-10 w-full max-w-md">
                <div className="mb-8 flex items-center justify-center">
                    <img
                        src="/brand/moovbytes-logo-white.png"
                        alt="MoovBytes"
                        className="h-8 w-auto select-none"
                    />
                </div>

                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-8">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/60 to-transparent" />

                    {done ? (
                        <div data-testid="reset-success">
                            <CheckCircle2 size={32} className="text-[#10b981]" />
                            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">
                                Password updated
                            </h1>
                            <p className="mt-2 text-sm text-white/65">
                                Redirecting you to sign in…
                            </p>
                        </div>
                    ) : tokenMissing ? (
                        <div data-testid="reset-missing-token">
                            <h1 className="font-display text-2xl font-bold tracking-tight">
                                Missing reset token
                            </h1>
                            <p className="mt-2 text-sm text-white/65">
                                This page needs a one-time link from your reset email.
                                Request a new one below.
                            </p>
                            <Link
                                to="/forgot-password"
                                data-testid="reset-request-new"
                                className="mt-6 inline-flex items-center justify-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-[#03122b] transition hover:bg-white/90"
                            >
                                Request new link
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h1 className="font-display text-2xl font-bold tracking-tight">
                                Choose a new password
                            </h1>
                            <p className="mt-2 text-sm text-white/55">
                                Make it at least 6 characters. Existing sessions
                                will be signed out for security.
                            </p>

                            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
                                <label className="block">
                                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-white/45">
                                        New password
                                    </span>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        data-testid="reset-password-input"
                                        className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white outline-none transition focus:border-[#00C2FF]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#00C2FF]/20"
                                    />
                                </label>
                                <label className="block">
                                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-white/45">
                                        Confirm password
                                    </span>
                                    <input
                                        type="password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        minLength={6}
                                        data-testid="reset-confirm-input"
                                        className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white outline-none transition focus:border-[#00C2FF]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#00C2FF]/20"
                                    />
                                </label>

                                {err && (
                                    <div
                                        data-testid="reset-error"
                                        className="rounded-lg border border-[#ef4444]/40 bg-[#ef4444]/10 px-3 py-2 text-xs text-[#fca5a5]"
                                    >
                                        {err}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="reset-submit-btn"
                                    className="inline-flex w-full items-center justify-center rounded-lg bg-white px-3 py-3 text-sm font-semibold text-[#03122b] shadow-lg transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {loading ? "Updating…" : "Update password"}
                                </button>
                            </form>
                        </>
                    )}

                    <Link
                        to="/login"
                        data-testid="reset-back-link"
                        className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 transition hover:text-white"
                    >
                        <ArrowLeft size={12} /> Back to log in
                    </Link>
                </div>
            </div>
        </div>
    );
}
